import Badge from '@atlaskit/badge';
import Button from '@atlaskit/button';
import FilterIcon from '@atlaskit/icon/glyph/filter';
import { Inline } from '@atlaskit/primitives';
import { xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { SortIcon, HideIcon } from '@databases/icons';
import usePanel from '@databases/panel/usePanel';
import { DatabaseFilterUsageType, DatabaseFilter } from '@databases/types/database/view';
import { getHiddenFields } from '@databases/utils/database/view/view';
import { messages } from '@databases/views/messages';
import { ViewPanelSettingType } from '@databases/views/types';
import useView from '@databases/views/useView';
import { useViewUiState } from '@databases/views/useViewUiState';
import { getLayoutIcons } from '@databases/views/utils';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

const viewSettingsButtonsStyles = xcss({
  paddingBottom: 'space.100',
  paddingLeft: 'space.050',
  marginLeft: 'space.050',
});

export interface ViewSettingsButtonsProps {
  hasNoFields: boolean;
  openViewSettingsPanel: (activeViewSetting: ViewPanelSettingType | null) => void;
}

const ViewSettingsButtons: React.FC<ViewSettingsButtonsProps> = ({
  hasNoFields,
  openViewSettingsPanel,
}) => {
  const { formatMessage } = useIntl();

  const { view } = useView();
  const { activePanelSetting } = useViewUiState();
  const { isPanelVisible, hidePanel } = usePanel();

  const filters = view.settings.filter as
    | {
        type: DatabaseFilterUsageType;
        value: DatabaseFilter[];
      }
    | undefined;
  const layoutIcons = getLayoutIcons(view.settings.layout.type, 'medium');

  const isViewSettingActiveInPanel = useCallback(
    (viewSetting: ViewPanelSettingType) => {
      return isPanelVisible('ViewSettingsPanel') && activePanelSetting === viewSetting;
    },
    [activePanelSetting, isPanelVisible]
  );

  const handleButtonClick = useCallback(
    (viewSetting: ViewPanelSettingType) => {
      if (isViewSettingActiveInPanel(viewSetting)) {
        hidePanel();
      } else {
        openViewSettingsPanel(viewSetting ?? null);
      }
    },
    [isViewSettingActiveInPanel, openViewSettingsPanel, hidePanel]
  );

  return (
    <Inline space="space.100" shouldWrap xcss={viewSettingsButtonsStyles}>
      <Button
        spacing="compact"
        appearance="subtle"
        isSelected={isViewSettingActiveInPanel(ViewPanelSettingType.LAYOUT)}
        iconBefore={layoutIcons.icon}
        onClick={() => handleButtonClick(ViewPanelSettingType.LAYOUT)}
        testId={'view-settings-layout-button'}
      >
        {formatMessage(messages['view-settings.layout.layout-text'], {
          layoutType: formatMessage(layoutIcons.label),
        })}
      </Button>
      <Button
        spacing="compact"
        appearance="subtle"
        isSelected={isViewSettingActiveInPanel(ViewPanelSettingType.FILTER) || !!filters}
        iconBefore={<FilterIcon label="" />}
        onClick={() => handleButtonClick(ViewPanelSettingType.FILTER)}
        testId={'view-settings-filter-button'}
      >
        <Inline alignBlock="center" space="space.050">
          {formatMessage(messages['view-settings.filter.filter-text'])}
          {filters && (
            <Badge
              style={{
                backgroundColor: token(
                  'color.background.accent.blue.subtlest.hovered',
                  '#CCE0FF'
                ),
              }}
              testId="filters-badge"
            >
              {filters.value.length}
            </Badge>
          )}
        </Inline>
      </Button>
      <Button
        spacing="compact"
        appearance="subtle"
        isSelected={
          isViewSettingActiveInPanel(ViewPanelSettingType.SORT) || !!view.settings.sort
        }
        isDisabled={hasNoFields}
        iconBefore={<SortIcon label="" />}
        onClick={() => handleButtonClick(ViewPanelSettingType.SORT)}
        testId={'view-settings-sort-button'}
      >
        {formatMessage(messages['view-settings.sort.sort-text'])}
        {/* TODO NCTD-1499 tooltip for number of sorts */}
      </Button>
      <Button
        spacing="compact"
        appearance="subtle"
        iconBefore={<HideIcon label="" />}
        isSelected={
          isViewSettingActiveInPanel(ViewPanelSettingType.FIELDS) ||
          !!view.settings.fields
        }
        isDisabled={hasNoFields}
        onClick={() => handleButtonClick(ViewPanelSettingType.FIELDS)}
        testId={'view-settings-hide-field-button'}
      >
        <Inline alignBlock="center" space="space.050">
          {formatMessage(messages['view-settings.hide-field.field-text'])}
          {view.settings.fields && (
            <Badge
              style={{
                backgroundColor: token(
                  'color.background.accent.blue.subtlest.hovered',
                  '#CCE0FF'
                ),
              }}
              testId="fields-badge"
            >
              {getHiddenFields(view.settings).length}
            </Badge>
          )}
        </Inline>
      </Button>
    </Inline>
  );
};

export default ViewSettingsButtons;
